/**
 * Card collection for the annual membership (ELARA-1012).
 *
 * Rendered as the third phase of signup, once the account and profile exist and
 * `StartMembershipPayment` has returned a client secret. Card details go straight from the Stripe
 * Payment Element to Stripe — they never touch Elara, and Elara is never in scope for them.
 *
 * Note this is a different payment rail from `CardCaptureFrame`, which uses PCI Booking to vault a
 * card for paying hotels. Membership billing is Stripe. The two coexist on purpose; do not try to
 * unify them.
 *
 * A member with cards already on file — anyone restarting a membership they once cancelled — gets
 * them listed above the new-card fields, with the new card still one click away. That list is the
 * Element's own, unlocked by the Customer Session secret the server mints with the collection; there
 * is no chooser of ours, and `confirmSetup` below is identical whichever the member picks.
 *
 * `redirect: 'if_required'` keeps the common case inline: most cards confirm without leaving the
 * page. A card that demands 3DS redirects to `return_url` and comes back to the payment route,
 * which picks the flow back up from the `payment_intent_client_secret` Stripe appends.
 *
 * The price shown here comes from `StartMembershipPayment`, which reads it off the very invoice
 * this form confirms — never from a constant in the client. That is what guarantees the figure a
 * member agrees to is the figure Stripe takes, coupons and tax included.
 *
 * Styling is split by what owns the pixels. Everything outside the Element is ordinary Tailwind;
 * the fields inside it are cross-origin and reachable only through Stripe's Appearance API, which
 * `elaraStripeAppearance()` builds from the same design tokens the rest of the form uses. What the
 * Element offers is narrowed server-side instead — the subscription names
 * `payment_method_types: ['card']`, which is what removes the alternative-method tabs and the Link
 * "save my information" block.
 */

import type { StripePaymentElementOptions } from '@stripe/stripe-js';

import type { MembershipPaymentState, PriceParts } from '@elara/client-core/states/MembershipPaymentState';
import type { IconName } from '@elara/client-core/ui-contracts/icon-name';

import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import React from 'react';

import { trackAddPaymentInfo } from '@elara/client-core/lib/analytics-events';

import { getStripe, isStripeConfigured } from '#lib/stripe.js';
import { elaraStripeAppearance, TYPEKIT_CSS_SRC } from '#lib/stripe-appearance.js';
import { Alert, Button, IconSizes, Loader, MaterialIcon } from '#ui/index.js';

/**
 * `layout: 'accordion'` with a single payment method collapses to plain card fields — no tab strip
 * above them, since the subscription is pinned to `payment_method_types: ['card']` server-side and
 * there is nothing to tab between.
 *
 * `defaultCollapsed: false` keeps the fields open on arrival rather than behind a disclosure.
 *
 * `radios: 'if_multiple'` where this once said `'never'`. That was right when a card form was the
 * only thing this Element could ever show, and it is wrong now: with a Customer Session the Element
 * lists the member's saved cards above the new-card fields, and a list of alternatives with no radio
 * beside them is not a chooser. `'if_multiple'` states exactly the rule that matters — a chooser
 * only where there is something to choose — so a member with nothing saved still sees one item and
 * no radio, precisely what they saw before. The original intent, never showing a tab strip of
 * alternative payment METHODS, is held server-side by `payment_method_types: ['card']`.
 */
const PAYMENT_ELEMENT_OPTIONS: StripePaymentElementOptions = {
  layout: { type: 'accordion', defaultCollapsed: false, radios: 'if_multiple', spacedAccordionItems: false },
  /**
   * No Link. A membership is a recurring charge on a card we store ourselves, and Link's inline
   * "save my info for 1-click checkout" block asks the member to open an account with Stripe in the
   * middle of opening one with us — for no benefit to either side.
   *
   * Stated HERE rather than relied upon server-side. Naming `payment_method_types: ['card']` on the
   * intent does suppress Link when the Element is confirming a subscription's invoice, which is how
   * this was held before, but that was a side effect of the intent's shape: the moment the Element
   * moved to confirming a SetupIntent, Link came back. The Element is the surface that renders it,
   * so this is the place that decides.
   *
   * Apple Pay and Google Pay are deliberately left alone: they are card payments, they store a card
   * exactly as the form does, and they are faster for the member.
   */
  wallets: { link: 'never' },
};

interface MembershipPaymentFormProps {
  state: MembershipPaymentState;
  /** Where Stripe should return a 3DS redirect. Must be an absolute URL. */
  returnUrl: string;
  /** Called once the membership is active, so signup can move on. */
  onActivated: () => void;
}

/**
 * The inner form. Split out because `useStripe`/`useElements` only work beneath `<Elements>`, and
 * `<Elements>` needs the client secret that the outer component waits for.
 */
/**
 * The plan card, or one card per plan when more than one is on offer.
 *
 * Everything here is data: the name, the benefits and their icons come from the plan catalog via
 * `GetMembershipPlans`, and the price and interval come from Stripe. That is what makes a future
 * billing-cadence or trial experiment a config change rather than an edit to this file — nothing
 * below names a plan, an interval or a benefit.
 *
 * With one plan it states what the member is buying. With several it becomes a chooser: picking one
 * re-quotes against Stripe, because the amount actually charged depends on the plan's price, its
 * trial and any adjustment on the customer.
 */
function MembershipPlanCards({ state }: { state: MembershipPaymentState }) {
  const selectedId = state.planId;
  const plans = state.offeredPlans.length > 0 ? state.offeredPlans : undefined;

  // Nothing to show until the plan list has arrived, or at least a plan is known.
  if (!plans && !state.plan) return;

  // No plan list — the fetch failed, or billing is only partly configured. Fall back to stating the
  // quoted plan rather than rendering an empty chooser.
  if (!plans) {
    return (
      <PlanCard
        state={state}
        name={state.plan?.name}
        benefits={state.plan?.benefits}
        selected
        trialDays={state.trialDays}
      />
    );
  }

  return (
    <div className={plans.length > 1 ? 'grid gap-3' : undefined}>
      {plans.map((plan) => {
        const isSelected = plan.id === selectedId;
        return (
          <PlanCard
            key={plan.id}
            state={state}
            name={plan.name}
            benefits={plan.benefits}
            selected={isSelected}
            // A single plan is a statement, not a choice, so it gets no click target. Selecting is
            // local — nothing in Stripe corresponds to it until the card is confirmed.
            onSelect={plans.length > 1 && !isSelected ? () => state.selectPlan(plan.id) : undefined}
            // Every card quotes its own plan's price, straight off the Stripe Price. There is no
            // separate "what you will be charged" figure to reconcile any more, because nothing is
            // created until the member commits.
            price={state.listPrice(plan)}
            // Through the store, not off the plan: a returning member gets no trial, and the card
            // must not promise one the server will withhold.
            trialDays={state.trialDaysFor(plan)}
          />
        );
      })}
    </div>
  );
}

function PlanCard(props: {
  state: MembershipPaymentState;
  name?: string;
  benefits?: ReadonlyArray<{ icon: string; text: string }>;
  selected: boolean;
  onSelect?: () => void;
  price?: PriceParts;
  trialDays?: number;
}) {
  const { state, name, selected, onSelect, trialDays } = props;
  const benefits = props.benefits && state.benefitsFor(props.benefits);
  const price = props.price ?? (selected ? state.price : undefined);

  const body = (
    <>
      {selected && (
        <MaterialIcon icon="check_circle" filled size={IconSizes.md} className="absolute top-4 right-4 text-accent" />
      )}
      <div className="text-sm font-medium">{name}</div>
      {/* The amount is what a member compares between cards, so it carries the weight; the period
          qualifies it and steps back. Both sit on one baseline: `items-baseline`, not `items-end`,
          because the two sizes have different descenders and only the baseline reads as one line. */}
      {price && (
        <div className="flex items-baseline gap-1">
          <span className="text-2xl tabular-nums">{price.amount}</span>
          {price.period && <span className="text-sm text-muted-foreground">/ {price.period}</span>}
        </div>
      )}
      {/* The trial sits directly under the price, because it changes what the price MEANS: nothing
          today, that figure from the day the trial ends. It is the most consequential fact on the
          card for the member choosing it, so it gets the weight of a headline rather than a footnote.
          Only the selected card carries it in this form; an unselected one gets the short hint below. */}
      {selected && trialDays ? (
        <div className="text-sm font-medium text-green-700 dark:text-white">Includes a {trialDays}-day free trial</div>
      ) : undefined}
      {/* Benefits and terms belong to the plan being bought, so only the selected card carries
          them. Plans mostly share their benefits, and repeating an identical list under every card
          buries the one thing a chooser exists to compare — the price and the interval. */}
      {selected && benefits && benefits.length > 0 && (
        <ul className="text-sm my-3 flex flex-col gap-1.5">
          {benefits.map((benefit) => (
            <li key={benefit.text} className="flex gap-2 items-center">
              {/* The catalog holds the icon as an opaque string so the domain layer stays free of
                  either platform's icon set; the web shell is where it becomes an IconName. An
                  unrecognised name renders as the Material Symbols ligature failing to resolve —
                  no glyph, card intact — which is the right failure for content metadata. */}
              <MaterialIcon icon={benefit.icon as IconName} className="text-muted-foreground" />
              <span>{benefit.text}</span>
            </li>
          ))}
        </ul>
      )}
      {/* The renewal note closes the selected card whether or not it trials. On a trial the price
          above is what renews once the trial ends, and the trial line under it has already said so;
          repeating the figure here would make the card read as two different offers. */}
      {selected && <p className="text-xs text-muted-foreground">Membership renews automatically until cancelled.</p>}
      {/* The one term an UNSELECTED card still states: a trial changes what picking it would cost
          today, which is part of the choice rather than of the plan already chosen. */}
      {!selected && trialDays ? (
        <p className="text-xs text-green-700 dark:text-white">Includes a {trialDays}-day free trial</p>
      ) : undefined}
    </>
  );

  const className = `relative space-y-1 p-4 rounded-md border bg-background text-left ${
    selected ? 'border-accent ring ring-accent' : 'border-input'
  }`;

  if (!onSelect) return <div className={className}>{body}</div>;
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={state.isBusy}
      // `cursor-pointer` because Tailwind's preflight leaves buttons at the default arrow, and a
      // card that switches plan on click has to look clickable before it is clicked.
      className={`${className} w-full cursor-pointer disabled:cursor-not-allowed`}
    >
      {body}
    </button>
  );
}

function PaymentFields({ state, returnUrl, onActivated }: MembershipPaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isReady, setIsReady] = React.useState(false);

  // What the click actually does. A trial stores the card and charges nothing today, so promising
  // a sum there would be a false promise on the one control that commits the member.
  //
  // "Restart" for a returning member, matching the page heading. A returning member never sees the
  // trial branch — one trial per customer — so only the two paid labels need the verb.
  const verb = state.isReturning ? 'restart' : 'start';
  const payLabel = state.trialDays
    ? 'Start free trial'
    : state.amountDueNowLabel
      ? `Pay ${state.amountDueNowLabel} and ${verb} membership`
      : `${verb === 'restart' ? 'Restart' : 'Start'} membership`;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements || state.isBusy) return;

    state.markConfirming();
    trackAddPaymentInfo({ paymentType: 'card' });

    // One confirmation for every plan, trial or not: what is being confirmed is a SetupIntent that
    // STORES the card. No subscription exists yet, so a decline here costs nothing and leaves
    // nothing behind — the member simply tries another card.
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });

    if (error) {
      // Stripe's own message is the useful one — "your card was declined" beats anything generic.
      state.failConfirmation(error.message ?? 'That card could not be saved. Please try again.');
      return;
    }

    // Mid-redirect for 3DS: the browser is already navigating and the return leg finishes the job.
    if (!setupIntent || setupIntent.status === 'requires_action') return;

    // The card exists. This is where the membership is actually created.
    if (await state.completePayment(setupIntent.id)) {
      onActivated();
      return;
    }

    // The one outcome that continues here: the issuer wants the first charge authenticated too.
    // The member is still on the screen, so finish it now rather than emailing them later.
    const actionSecret = state.pendingActionSecret;
    if (!actionSecret) return;

    const { error: actionError } = await stripe.confirmPayment({
      clientSecret: actionSecret,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });

    if (actionError) {
      state.failConfirmation(actionError.message ?? 'That payment could not be authenticated.');
      return;
    }

    if (await state.markAuthenticated()) onActivated();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {state.error && (
        <Alert variant="destructive">
          <span className="whitespace-pre-line">{state.error}</span>
        </Alert>
      )}

      <MembershipPlanCards state={state} />

      <PaymentElement options={PAYMENT_ELEMENT_OPTIONS} onReady={() => setIsReady(true)} />

      <div className="flex flex-col">
        <Button type="submit" size="lg" disabled={!isReady || state.isBusy} className="w-full">
          {/* The amount is on the button as well as the summary: it is the last thing read before a
              charge, and a member should never have to scroll back up to check what they agreed to. */}
          {state.phase === 'confirming' ? 'Confirming…' : payLabel}
        </Button>
      </div>

      <p className="text-xs text-center text-muted-foreground">
        Card details are handled by Stripe and never reach Elara.
      </p>
    </form>
  );
}

/**
 * Loads Stripe.js, waits for the client secret, and renders the card fields.
 *
 * Both "no publishable key" and "activating" are handled here rather than inside the form, because
 * neither can render a Payment Element: the first has no Stripe, and the second has already handed
 * the card details over and is waiting on Stripe to settle.
 */
export function MembershipPaymentForm(props: MembershipPaymentFormProps) {
  const { state } = props;
  const [stripe, setStripe] = React.useState<Awaited<ReturnType<typeof getStripe>>>(null);
  const [loadFailed, setLoadFailed] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    getStripe()
      .then((loaded) => {
        if (!cancelled) setStripe(loaded);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!isStripeConfigured() || loadFailed) {
    return (
      <Alert variant="destructive">
        Payments are not available right now. Your account has been created — please contact us and we will finish
        setting up your membership.
      </Alert>
    );
  }

  // One server call, not a wait: the subscription is created there and its status comes back with
  // the response, so this spinner is only ever up for the length of a request.
  if (state.phase === 'activating' || state.phase === 'active') {
    return (
      <div className="flex items-center gap-3 py-8 text-center">
        <Loader loading />
        <p className="text-muted-foreground">Starting your membership…</p>
      </div>
    );
  }

  // A prepare failure has no secret to render against, so the message is all there is to show.
  if (state.phase === 'failed' && !state.clientSecret) {
    return <Alert variant="destructive">{state.error}</Alert>;
  }

  if (!stripe || !state.clientSecret) {
    return (
      <div className="flex justify-center py-8">
        <Loader loading />
      </div>
    );
  }

  return (
    <Elements
      // Keyed by the secret so a fresh collection REMOUNTS the Element. `<Elements>` will not accept
      // a changed `clientSecret` after mount, and the secret does change mid-session: a declined
      // first charge spends the SetupIntent, and the store opens a new one for the next card.
      key={state.clientSecret}
      stripe={stripe}
      options={{
        clientSecret: state.clientSecret,
        // What makes the Element offer the cards this member already has as well as a new one — the
        // case a returning member restarting their membership is in. Undefined is ordinary (a
        // first-time member, or a refused session) and simply yields the plain card form.
        ...(state.customerSessionClientSecret
          ? { customerSessionClientSecret: state.customerSessionClientSecret }
          : {}),
        appearance: elaraStripeAppearance(),
        // The iframe cannot see the page's webfonts, so Stripe loads the same Typekit sheet itself.
        fonts: [{ cssSrc: TYPEKIT_CSS_SRC }],
      }}
    >
      <PaymentFields {...props} />
    </Elements>
  );
}
